/**
 * Client Details Page - Microsoft 365 Style
 * Displays comprehensive client information
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { makeStyles, Button, Text, Avatar, Spinner } from '@fluentui/react-components';
import { ArrowLeftRegular, EditRegular, BuildingRegular } from '@fluentui/react-icons';
import { ContentCard } from '../../components/layout';
import { ApiClient, newToast } from '../../configurations/index.config';
import { getErrorMessage } from '../../types';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
  },
  header: {
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    minWidth: 'auto',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  clientHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    padding: '24px',
    borderBottom: '1px solid #e1dfdd',
  },
  clientIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '12px',
    backgroundColor: '#008272',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    fontSize: '32px',
  },
  clientName: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
  },
  clientCategory: {
    fontSize: '14px',
    color: '#605e5c',
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    padding: '24px',
  },
  section: {
    marginBottom: '0',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '16px',
    paddingBottom: '0.5px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  fieldLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#605e5c',
    textTransform: 'uppercase',
  },
  fieldValue: {
    fontSize: '14px',
    color: '#323130',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  fullWidthSection: {
    gridColumn: '1 / -1',
  },
});

interface Client {
  id: string;
  client_name: string;
  gender?: string;
  email?: string;
  address?: string;
  phone?: string;
  tax_id?: string;
  category_id?: string;
  category_name?: string;
  client_type?: string;
  contact_name?: string;
  contact_gender?: string;
  contact_phone?: string;
  contact_email?: string;
  contact_role?: string;
}

export const ClientDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const [client, setClient] = useState<Client | null>(location.state?.client || null);
  const [loading, setLoading] = useState(!location.state?.client);

  const fetchClientDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-clients');
      if (response.data.status === 'success') {
        const clients = response.data.data || [];
        const foundClient = clients.find((c: Client) => c.id === id);

        if (foundClient) {
          setClient(foundClient);
        } else {
          newToast('error', 'Client not found');
          navigate('/account/clients');
        }
      } else {
        newToast('error', response.data?.message || 'Failed to fetch client details');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    if (!client && id) {
      fetchClientDetails();
    }
  }, [client, id, fetchClientDetails]);

  const handleBack = () => {
    navigate('/account/clients');
  };

  const handleEdit = () => {
    if (client) {
      navigate('/account/add-client', { state: { client } });
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading client details..." />
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Text size={500} weight="semibold">
            Client not found
          </Text>
          <Button appearance="primary" onClick={handleBack} style={{ marginTop: '16px' }}>
            Back to Clients
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <Button
            icon={<ArrowLeftRegular />}
            appearance="subtle"
            onClick={handleBack}
            className={styles.backButton}
          />
          <Text className={styles.pageTitle}>Client Details</Text>
        </div>
        <div className={styles.headerActions}>
          <Button icon={<EditRegular />} appearance="primary" onClick={handleEdit}>
            Edit Client
          </Button>
        </div>
      </div>

      {/* Client Card */}
      <ContentCard>
        {/* Client Header */}
        <div className={styles.clientHeader}>
          <div className={styles.clientIcon}>
            <BuildingRegular />
          </div>
          <div>
            <Text className={styles.clientName}>{client.client_name}</Text>
            {client.category_name && (
              <Text className={styles.clientCategory}>{client.category_name}</Text>
            )}
          </div>
        </div>

        {/* Client Details Grid */}
        <div className={styles.contentGrid}>
          {/* Basic Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Basic Information</Text>
            <div className={styles.fieldGroup}>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Client ID</Text>
                <Text className={styles.fieldValue}>{client.id}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Client Name</Text>
                <Text className={styles.fieldValue}>{client.client_name}</Text>
              </div>
              <div className={styles.field}>
                <Text className={styles.fieldLabel}>Client Type</Text>
                <Text className={styles.fieldValue}>{client.client_type || '—'}</Text>
              </div>
              {client.gender && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Gender</Text>
                  <Text className={styles.fieldValue}>{client.gender}</Text>
                </div>
              )}
              {client.category_name && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Category</Text>
                  <Text className={styles.fieldValue}>{client.category_name}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className={styles.section}>
            <Text className={styles.sectionTitle}>Contact Information</Text>
            <div className={styles.fieldGroup}>
              {client.email && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Email</Text>
                  <Text className={styles.fieldValue}>
                    <a
                      href={`mailto:${client.email}`}
                      style={{ color: '#0078d4', textDecoration: 'none' }}
                    >
                      {client.email}
                    </a>
                  </Text>
                </div>
              )}
              {client.phone && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Phone</Text>
                  <Text className={styles.fieldValue}>
                    <a
                      href={`tel:${client.phone}`}
                      style={{ color: '#0078d4', textDecoration: 'none' }}
                    >
                      {client.phone}
                    </a>
                  </Text>
                </div>
              )}
              {client.address && (
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Address</Text>
                  <Text className={styles.fieldValue}>{client.address}</Text>
                </div>
              )}
            </div>
          </div>

          {/* Financial Information */}
          {client.tax_id && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Financial Information</Text>
              <div className={styles.fieldGroup}>
                <div className={styles.field}>
                  <Text className={styles.fieldLabel}>Tax ID</Text>
                  <Text className={styles.fieldValue}>{client.tax_id}</Text>
                </div>
              </div>
            </div>
          )}

          {/* Contact Person */}
          {(client.contact_name || client.contact_email || client.contact_phone) && (
            <div className={styles.section}>
              <Text className={styles.sectionTitle}>Contact Person</Text>
              <div className={styles.fieldGroup}>
                {client.contact_name && (
                  <div className={styles.field}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '8px',
                      }}
                    >
                      <Avatar name={client.contact_name} color="brand" size={48} />
                      <div>
                        <Text className={styles.fieldValue} style={{ fontWeight: '600' }}>
                          {client.contact_name}
                        </Text>
                        {client.contact_role && (
                          <Text style={{ fontSize: '12px', color: '#605e5c', display: 'block' }}>
                            {client.contact_role}
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {client.contact_gender && (
                  <div className={styles.field}>
                    <Text className={styles.fieldLabel}>Gender</Text>
                    <Text className={styles.fieldValue}>{client.contact_gender}</Text>
                  </div>
                )}
                {client.contact_email && (
                  <div className={styles.field}>
                    <Text className={styles.fieldLabel}>Email</Text>
                    <Text className={styles.fieldValue}>
                      <a
                        href={`mailto:${client.contact_email}`}
                        style={{ color: '#0078d4', textDecoration: 'none' }}
                      >
                        {client.contact_email}
                      </a>
                    </Text>
                  </div>
                )}
                {client.contact_phone && (
                  <div className={styles.field}>
                    <Text className={styles.fieldLabel}>Phone</Text>
                    <Text className={styles.fieldValue}>
                      <a
                        href={`tel:${client.contact_phone}`}
                        style={{ color: '#0078d4', textDecoration: 'none' }}
                      >
                        {client.contact_phone}
                      </a>
                    </Text>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ContentCard>
    </div>
  );
};

export default ClientDetailsFluentUI;
